import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Work as CVIcon,
  Book as StoriesIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import CVSectionDialog from '../components/CVSectionDialog';
import CVItemDialog from '../components/CVItemDialog';
import StoriesManagement from '../components/StoriesManagement';

const Admin = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  useEffect(() => {
    if (activeTab === 0) {
      fetchSections();
    }
  }, [activeTab]);

  const fetchSections = async () => {
    try {
      const response = await axios.get('/api/cv/admin/sections');
      setSections(response.data.sections);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setError('Failed to load CV sections');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section and all its items?')) {
      return;
    }

    try {
      await axios.delete(`/api/cv/admin/sections/${sectionId}`);
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      setError('Failed to delete section');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`/api/cv/admin/items/${itemId}`);
      fetchSections();
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item');
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setSectionDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleAddItem = (sectionId) => {
    setSelectedSectionId(sectionId);
    setEditingItem(null);
    setItemDialogOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading CV sections...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={logout}
        >
          Logout
        </Button>
      </Box>

      {user && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome back, {user.username}! You can manage your content here.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<CVIcon />} label="CV Management" />
          <Tab icon={<StoriesIcon />} label="Stories" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">CV Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingSection(null);
                setSectionDialogOpen(true);
              }}
            >
              Add Section
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

      {sections.map((section) => (
        <Card key={section.id} sx={{ mb: 2 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box>
                  <Typography variant="h6">{section.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={section.is_active ? 'Active' : 'Inactive'} 
                    color={section.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSection(section);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSection(section.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Items ({section.items.length})</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem(section.id)}
                  >
                    Add Item
                  </Button>
                </Box>
                
                {section.items.length === 0 ? (
                  <Typography color="text.secondary">No items yet</Typography>
                ) : (
                  section.items.map((item) => (
                    <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="h6">{item.title}</Typography>
                            {item.subtitle && (
                              <Typography color="text.secondary" gutterBottom>
                                {item.subtitle}
                              </Typography>
                            )}
                            {item.description && (
                              <Typography variant="body2" paragraph>
                                {item.description}
                              </Typography>
                            )}
                            {(item.start_date || item.end_date) && (
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(item.start_date)} - {item.end_date ? formatDate(item.end_date) : 'Present'}
                              </Typography>
                            )}
                            {item.location && (
                              <Typography variant="body2" color="text.secondary">
                                📍 {item.location}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={item.is_active ? 'Active' : 'Inactive'} 
                              color={item.is_active ? 'success' : 'default'}
                              size="small"
                            />
                            <IconButton size="small" onClick={() => handleEditItem(item)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteItem(item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Card>
      ))}
        </Box>
      )}

      {activeTab === 1 && (
        <StoriesManagement />
      )}

      <CVSectionDialog
        open={sectionDialogOpen}
        onClose={() => {
          setSectionDialogOpen(false);
          setEditingSection(null);
        }}
        section={editingSection}
        onSave={fetchSections}
      />

      <CVItemDialog
        open={itemDialogOpen}
        onClose={() => {
          setItemDialogOpen(false);
          setEditingItem(null);
          setSelectedSectionId(null);
        }}
        item={editingItem}
        sectionId={selectedSectionId}
        sections={sections}
        onSave={fetchSections}
      />
    </Container>
  );
};

export default Admin;